"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { RemittanceConfig } from "./types";

const RemittanceConfigContext = createContext<RemittanceConfig | null>(null);

export interface RemittanceConfigProviderProps {
  config: RemittanceConfig;
  children: ReactNode;
}

/**
 * Provider component that makes remittance config available to all child components.
 * Wrap your app or a section of your app with this provider.
 *
 * @example
 * ```tsx
 * import { RemittanceConfigProvider } from "cy-remittance-config";
 * import { remittanceConfig } from "@/config/remittance";
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <RemittanceConfigProvider config={remittanceConfig}>
 *       {children}
 *     </RemittanceConfigProvider>
 *   );
 * }
 * ```
 */
export function RemittanceConfigProvider({
  config,
  children,
}: RemittanceConfigProviderProps) {
  return (
    <RemittanceConfigContext.Provider value={config}>
      {children}
    </RemittanceConfigContext.Provider>
  );
}

/**
 * Hook to access remittance config from any component within the provider.
 * Throws an error if used outside of RemittanceConfigProvider.
 *
 * @example
 * ```tsx
 * import { useRemittanceConfig } from "cy-remittance-config";
 *
 * function AccountLimitsDisplay() {
 *   const { accountLimits } = useRemittanceConfig();
 *   return (
 *     <ul>
 *       {accountLimits.map((limit) => (
 *         <li key={limit.id}>{limit.name}: {limit.description}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useRemittanceConfig(): RemittanceConfig {
  const config = useContext(RemittanceConfigContext);
  if (!config) {
    throw new Error(
      "useRemittanceConfig must be used within a RemittanceConfigProvider. " +
        "Wrap your component tree with <RemittanceConfigProvider config={...}>.",
    );
  }
  return config;
}

/**
 * Hook to access remittance config, returning null if not within a provider.
 * Useful for components that need to work both with and without config.
 */
export function useRemittanceConfigOptional(): RemittanceConfig | null {
  return useContext(RemittanceConfigContext);
}
